import { useOktaAuth } from '@okta/okta-react';
import { useMutation } from '@tanstack/react-query';
import { App, Form } from 'antd';
import { AUTH_SERVICES_BASE_URL, appPermissionUrl } from '@constants/url';
import useToken from '@hooks/useToken';
import { putFetchWithRenewal } from '@utils/put-fetch-with-renewal';

/** The permission is named in the path, so these two are the whole request. */
export type AppPermissionFormValues = {
	cid: string;
	permission: string;
};

const useAppPermissionForm = () => {
	const token = useToken();
	const { oktaAuth } = useOktaAuth();
	const { message } = App.useApp();

	// The form instance lives here so the hook can clear it once a grant lands.
	const [form] = Form.useForm<AppPermissionFormValues>();

	const { mutate: grantPermission, isPending: isSubmitting } = useMutation({
		mutationFn: async ({ cid, permission }: AppPermissionFormValues) => {
			if (!AUTH_SERVICES_BASE_URL) {
				throw new Error('Auth services URL is not configured');
			}

			// Everything the endpoint needs is in the path; there's no body.
			return await putFetchWithRenewal<unknown>(
				oktaAuth,
				token,
				appPermissionUrl(cid.trim(), permission)
			);
		},
		onSuccess: () => {
			message.success('Permission granted');
			// One app is likely to be granted more than one of these, so the
			// client id stays put and only the permission clears.
			form.resetFields(['permission']);
		},
		onError: (error) => {
			message.error(error instanceof Error ? error.message : 'Request failed');
		},
	});

	return {
		form,
		grantPermission,
		isSubmitting,
	};
};

export default useAppPermissionForm;
